import { localDateInputLength } from "_shared/dates/date-input";
import type { CalendarEventResponse, CalendarRecurrence } from "calendar/_schemas/calendar-event-schema";

const safeExternalLinkProtocols = new Set(["http:", "https:"]);
const recurrenceUnitLabels = {
  daily: "days",
  weekly: "weeks",
  monthly: "months",
  yearly: "years",
} as const;

export function getSafeExternalLink(link: string): string | undefined {
  try {
    const parsed = new URL(link);
    return safeExternalLinkProtocols.has(parsed.protocol) ? parsed.toString() : undefined;
  } catch {
    return undefined;
  }
}

export function formatEventRange(event: Pick<CalendarEventResponse, "end" | "isAllDay" | "start">): string {
  if (event.isAllDay) {
    const start = parseLocalDate(String(event.start));
    if (event.end === null) return formatDate(start);
    const inclusiveEnd = parseLocalDate(String(event.end));
    inclusiveEnd.setDate(inclusiveEnd.getDate() - 1);
    return start.getTime() === inclusiveEnd.getTime()
      ? formatDate(start)
      : `${formatDate(start)} – ${formatDate(inclusiveEnd)}`;
  }

  const start = new Date(String(event.start));
  const end = event.end === null ? undefined : new Date(String(event.end));
  return end ? `${formatDateTime(start)} – ${formatDateTime(end)}` : formatDateTime(start);
}

export function formatRecurrence(recurrence: CalendarRecurrence | null, isAllDay: boolean): string {
  if (!recurrence) return "Does not repeat";
  const frequency = recurrence.frequency.charAt(0).toUpperCase() + recurrence.frequency.slice(1);
  const interval = recurrence.interval === 1
    ? frequency
    : `Every ${recurrence.interval} ${recurrenceUnitLabels[recurrence.frequency]}`;
  const weekdays = recurrence.weekdays.length > 0
    ? ` on ${recurrence.weekdays.map((weekday) => weekday.charAt(0).toUpperCase() + weekday.slice(1)).join(", ")}`
    : "";
  if (recurrence.count) return `${interval}${weekdays}, ${recurrence.count} occurrences`;
  if (recurrence.untilDate) return `${interval}${weekdays}, through ${formatDate(parseLocalDate(recurrence.untilDate))}`;
  if (recurrence.untilUtc) {
    const until = new Date(recurrence.untilUtc);
    return `${interval}${weekdays}, through ${isAllDay ? formatDate(until) : formatDateTime(until)}`;
  }
  return `${interval}${weekdays}`;
}

function parseLocalDate(value: string): Date {
  return new Date(`${value.slice(0, localDateInputLength)}T00:00:00`);
}

function formatDate(value: Date): string {
  return new Intl.DateTimeFormat(undefined, { dateStyle: "medium" }).format(value);
}

function formatDateTime(value: Date): string {
  return new Intl.DateTimeFormat(undefined, { dateStyle: "medium", timeStyle: "short" }).format(value);
}
